import { ContactDataProps } from 'types/ContactProps';

/**
 * Aplica máscara de telefone brasileiro (Telefone fixo, celular ou 0800):
 * - Padrão 0800/0300: 0800-878-2471
 * - Padrão Fixo / Celular: (XX) XXXX-XXXX ou (XX) XXXXX-XXXX
 */
export function formatPhoneNumber(value: string): string {
	const digits = value.replace(/\D/g, '').slice(0, 11);

	if (digits.length === 0) return '';

	// Formatação para números 0800 ou 0300
	if (digits.startsWith('0800') || digits.startsWith('0300')) {
		if (digits.length <= 4) return digits;
		if (digits.length <= 7) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
		return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
	}

	// Formatação padrão para DDD + Telefone fixo ou celular
	if (digits.length <= 2) return `(${digits}`;
	if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
	if (digits.length <= 10) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
	}
	return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

/**
 * Aplica máscara exclusiva para WhatsApp (apenas DDD + número):
 * - Padrão: (XX) XXXX-XXXX ou (XX) XXXXX-XXXX
 */
export function formatWhatsappNumber(value: string): string {
	const digits = value.replace(/\D/g, '').slice(0, 11);

	if (digits.length === 0) return '';
	if (digits.length <= 2) return `(${digits}`;
	if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
	if (digits.length <= 10) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
	}
	return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

/**
 * Remove qualquer caractere que não seja dígito numérico
 */
export function cleanDigits(value: string): string {
	return value.replace(/\D/g, '');
}

/**
 * Valida se o formato do e-mail é válido quando preenchido
 */
export function isValidEmail(email: string): boolean {
	if (!email || !email.trim()) return true;
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email.trim());
}

/**
 * Valida se o telefone possui formato válido:
 * - 0800 / 0300: exatamente 11 dígitos
 * - DDD + Fixo/Celular: 10 ou 11 dígitos
 */
export function isValidPhone(phone: string): boolean {
	if (!phone || !phone.trim()) return true;
	const digits = cleanDigits(phone);

	if (digits.startsWith('0800') || digits.startsWith('0300')) {
		return digits.length === 11;
	}

	return digits.length >= 10 && digits.length <= 11;
}

/**
 * Valida se o WhatsApp possui formato válido:
 * - NÃO aceita 0800 ou 0300
 * - Exige DDD + número convencional (10 ou 11 dígitos)
 */
export function isValidWhatsapp(whatsapp: string): boolean {
	if (!whatsapp || !whatsapp.trim()) return true;
	const digits = cleanDigits(whatsapp);

	if (digits.startsWith('0800') || digits.startsWith('0300')) {
		return false;
	}

	return digits.length >= 10 && digits.length <= 11;
}

export interface ContactValidationResult {
	isValid: boolean;
	errors: {
		phone?: string;
		whatsapp?: string;
		email?: string;
	};
}

/**
 * Valida todos os campos de contato antes do salvamento
 */
export function validateContactForm(
	data: ContactDataProps
): ContactValidationResult {
	const errors: ContactValidationResult['errors'] = {};

	if (data.phone && !isValidPhone(data.phone)) {
		const digits = cleanDigits(data.phone);
		if (digits.startsWith('0800') || digits.startsWith('0300')) {
			errors.phone = 'Número 0800 incompleto (ex: 0800-878-2471).';
		} else {
			errors.phone =
				'Telefone incompleto. Informe DDD + número ou 0800 (ex: (11) 4002-8922 ou 0800-878-2471).';
		}
	}

	if (data.whatsapp && !isValidWhatsapp(data.whatsapp)) {
		const digits = cleanDigits(data.whatsapp);
		if (digits.startsWith('0800') || digits.startsWith('0300')) {
			errors.whatsapp =
				'WhatsApp não aceita números 0800. Informe um número com DDD (ex: (11) 99999-9999).';
		} else {
			errors.whatsapp =
				'WhatsApp incompleto. Informe DDD + número (ex: (11) 99999-9999).';
		}
	}

	if (data.email && !isValidEmail(data.email)) {
		errors.email = 'E-mail com formato inválido (ex: contato@exemplo.com).';
	}

	return {
		isValid: Object.keys(errors).length === 0,
		errors
	};
}
